/* eslint-disable no-useless-catch */

import { InputPassword, InputEmail } from '@/components/inputs';

import Logo from '../../assets/login/logo.svg';
import ButtonLogo from '../../assets/login/login-icon.gif';
import BG from '../../assets/login/bg.png';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { getToken, sendAuthRequest } from '@/app/api';
import { useAppDispatch } from '@/store/store';
import { setIsAuth } from '@/store/slices/authSlice.ts';
import { useOpenBrowser } from '@/hooks/useOpenBrowser';

/**
 * Renders the login page for the CloudCollect application.
 *
 * The page is divided into two sections:
 * - A form section with the CloudCollect logo, login inputs, and login button.
 * - A background image section for visual aesthetics.
 *
 * @returns {JSX.Element} The login page layout with form inputs and branding elements.
 */

const Login = (): JSX.Element => {
  const [email, setEmail] = useState<string>(''); // Состояние для email
  const [password, setPassword] = useState<string>(''); // Состояние для пароля
  const [errors, setErrors] = useState<{
    email: string;
    password: string;
    company: string;
  }>({
    email: '',
    password: '',
    company: '',
  }); // Ошибки от бэка
  const navigate = useNavigate(); // Перенаправление после логина
  const dispatch = useAppDispatch();

  const openReset = useOpenBrowser(`${import.meta.env.VITE_APP_URL}password/reset`);
  const openRegister = useOpenBrowser(`${import.meta.env.VITE_APP_URL}register`);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({ email: '', password: '', company: '' }); // Очистка ошибок

    try {
      const { csrf_token }: { csrf_token: string } = await getToken();
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      formData.append('_token', csrf_token);
      formData.append('remember', 'true');

      const response = await sendAuthRequest(formData);

      if (response?.status === 200) {
        // ✅ Успешный вход
        dispatch(setIsAuth(true));
        navigate('/'); // Перенаправляем пользователя
      
        if (window.electron) {
          window.electron.send('login');
        }
      } else if (response?.status === 403) {
        setErrors({
          email: '',
          password: '',
          company: response.data.error,
        });
      } else {
        setErrors(response?.data?.errors);
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#1F2B40]">
      <header className="h-[70px]">
        <div className="flex justify-between items-center p-5">
          <div className="flex gap-3 items-center">
            <img src={Logo} alt="logo" />
            <span className="font-roboto text-xl text-white">CloudCollect</span>
          </div>
        </div>
      </header>
      <main className="flex h-full flex-1 overflow-hidden flex-wrap md:flex-nowrap">
        <div className="w-full lg:w-1/2 p-6 md:p-12 text-center lg:rounded-tr-[56px] lg:rounded-br-[56px] bg-white flex flex-col justify-center items-center">
          <div className="w-full max-w-lg">
            <h2 className="text-[#1F2B40] text-2xl md:text-3xl lg:text-4xl pb-1 whitespace-nowrap">
              <span className="font-jostSemiBold border-b border-[#1F2B40]">
                Velkommen til CloudCollect
              </span>
            </h2>

            <h3 className="font-jostRegular text-[#1F2B40] mt-4 md:mt-6 text-2xl md:text-3xl">
              Log ind i systemet
            </h3>
            <div className="flex flex-col items-center mt-8 md:mt-10 gap-8 md:gap-12">
              <InputEmail
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                error={errors?.email}
                placeholder="Brugernavn"
              />
              <InputPassword
                error={errors?.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
                placeholder="Adgangskode"
              />
              <button
                onClick={handleSubmit}
                className="flex items-center bg-[#1F2B40] gap-3 justify-center text-[#BDE0F5] py-3 w-full max-w-[320px] md:w-[383px] rounded-3xl cursor-pointer">
                <img src={ButtonLogo} className="size-8" />
                <div className="font-jostSemiBold text-xl">Log ind</div>
              </button>
              {errors?.company && (
                <span
                  className={'text-[rgba(195,32,37,1)] text-lg leading-[24px]'}>
                  {errors?.company}
                </span>
              )}
              <div className="flex">
                <a
                  onClick={openReset}
                  className="text-[#1F2B40] text-lg md:text-xl underline cursor-pointer">
                  Glemt adgangskode
                </a>
              </div>
              <div className="flex gap-2 items-center text-[#1F2B40]">
                <span className="font-jostRegular text-lg md:text-xl">
                  Har du ikke en konto?
                </span>
                <a
                  onClick={openRegister}
                  className="underline text-lg md:text-xl font-jostSemiBold cursor-pointer">
                  Opret konto
                </a>
              </div>
              <div className="font-jostRegular text-[#1F2B40] text-lg md:text-xl">
                Eller ring på telefon 88 53 55 53
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex lg:w-1/2 h-full">
          <img src={BG} alt="bg" className="w-full h-full object-cover" />
        </div>
      </main>
    </div>
  );
};

export default Login;
